import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import NoRecordFoundComponent from "./NoRecords";
import { FileUploadServiceInternal } from "../../services/fileUpload.service";
import { toast } from "react-toastify";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import _lodash from "lodash";
import FullPageLoader from "./FullPageLoader";

interface FileUploadData {
  appointmentid: number;
  blobstoragepath: string;
  created: string;
  uploadedname: string;
  filename: string;
  userfileid: number;
}

interface FileUploadModelProps {
  open: boolean;
  onClose: () => void;
  handleFileUpload: (file: File) => void;
  appointmentId: number;
}

const FileUploadModel = ({
  open,
  onClose,
  handleFileUpload,
  appointmentId
}: FileUploadModelProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<any | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setFileName(file.name);
    }
  };

  const handleUploadButtonClick = () => {
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownloadFile = async (userFileId: number, fileName: string) => {
    const response = await FileUploadServiceInternal.downloadFile(userFileId);

    if (response?.data.isSuccess) {
      const data = response?.data.data;
      const byteCharacters = atob(data.contentBase64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/octet-stream" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error(response?.data.Message);
    }
    return response;
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete) {
      setIsDeleting(true);
      let deletedFileName = fileToDelete.fileName;
      try {
        const response = await FileUploadServiceInternal.deleteFile(fileToDelete.userFileId);
        if (response?.data.isSuccess) {
          fetchData();
          setIsDeleting(false);
          toast.success(`Deleted ${deletedFileName}.`);
        }
      }
      catch (error: any) {
        setIsDeleting(false);
        toast.error(error.message);
      }
      setConfirmDeleteOpen(false);
      setFileToDelete(null);
    }
  }

  const handleDeleteClick = (userFileId: number, fileName: string) => {
    setFileToDelete({
      userFileId: userFileId,
      fileName: fileName
    });
    setConfirmDeleteOpen(true);
  }

  const uploaddatetime = (date: any) => {
    const originalDate = new Date(date);
    // Convert UTC time to local timezone
    const localDate = new Date(
      originalDate.getTime() - originalDate.getTimezoneOffset() * 60000
    );

    const formattedDate = `${(localDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${localDate
      .getDate()
      .toString()
      .padStart(2, "0")}/${localDate.getFullYear()}`;
    const formattedTime = `${localDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
    return {
      date: formattedDate,
      time: formattedTime,
    };
  };

  const fetchData = async () => {
    try {
      const response = await FileUploadServiceInternal.getAllUploadedFile(
        appointmentId
      );
      if (response?.data.isSuccess) {
        let sortedData = _lodash.orderBy(
          response?.data.data,
          "created",
          sortOrder
        ); // Use lodash orderBy to sort
        setTableData(sortedData);
      } else {
        toast.error("No data found.");
      }
    } catch (error) {
      toast.error("Failed to fetch data. Please try again later.");
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortOrder]);

  useEffect(() => {
    setFile(null);
    setFileName(null)
    setFileToDelete(null)
    if (open) {
      fetchData(); // Fetch data only when the modal is open
    }
  }, [open]);

  return (
    <>
      {isDeleting && <FullPageLoader />}
      <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
        <DialogTitle></DialogTitle>
        <DialogActions>
          <Box display="flex" alignItems="center" gap={2}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {fileName && (
              <Typography variant="subtitle2" noWrap>
                {fileName}
              </Typography>
            )}
            <Button variant="outlined" color="primary" onClick={handleChooseFile}>
              Choose File
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            component="span"
            onClick={handleUploadButtonClick}
            disabled={!file}
          >
            Upload
          </Button>
          <Button onClick={onClose} color="error" variant="outlined">
            Close
          </Button>
        </DialogActions>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow
                style={{
                  color: "#A2A8BA",
                  backgroundColor: "#F6F7FB",
                }}
              >
                <TableCell className="first-cell">File Name</TableCell>
                <TableCell>
                  Upload Date Time
                  <Button onClick={handleSortOrder}>
                    {sortOrder === "desc" ? <ArrowUpward /> : <ArrowDownward />}
                  </Button>
                </TableCell>
                <TableCell>Upload By</TableCell>
                <TableCell className="text-center last-cell">Actions</TableCell>
              </TableRow>
            </TableHead>
            <br className="opacity-0" />
            {tableData.length > 0 ? (
              <TableBody>
                {tableData.map((row: FileUploadData) => (
                  <React.Fragment key={0}>
                    <TableRow
                      sx={{
                        borderRadius: "20px",
                        backgroundColor: "#FFFFFF",
                        borderWidth: "0",
                      }}
                    >
                      <TableCell>
                        <div>
                          <span className="quick-view-label">{row.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="quick-view-label">
                            {uploaddatetime(row.created).date}&nbsp;
                            {uploaddatetime(row.created).time}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="quick-view-label">
                            {row.uploadedname}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Tooltip title="Download File">
                          <IconButton
                            aria-label="download"
                            color="primary"
                            onClick={() =>
                              handleDownloadFile(row.userfileid, row.filename)
                            }
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete File">
                          <IconButton
                            aria-label="delete"
                            color="error"
                            onClick={() =>
                              handleDeleteClick(row.userfileid, row.filename)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <NoRecordFoundComponent cellSpan={7} />
              </TableBody>
            )}
          </Table>
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setFileToDelete(null);
        }}
      >
        <DialogTitle>Confrim Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{fileToDelete?.fileName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            component="span"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
          <Button
            onClick={() => {
              setConfirmDeleteOpen(false);
              setFileToDelete(null);
            }}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUploadModel;
